import * as React from 'react';
import pluralize from 'pluralize';
import Text from 'components/Text';
import LinkConstants from 'constants/LinkConstants';
import Link from 'components/Link';

type Props = {
  deviceName: string;
  deviceClasses: string[];
  displayLargeDevice: boolean;
};

export default function ClaimDeviceClasses(props: Props) {
  const hasClasses = () => props.deviceClasses.length > 0;

  function chunkDeviceClasses(deviceClasses: string[], splitAt: number = 2): [string[], string] {
    if (!deviceClasses.length) {
      return [[], ''];
    }

    if (deviceClasses.length === 1) {
      return [deviceClasses, ''];
    }

    const splitIndex = deviceClasses.length > splitAt ? splitAt : deviceClasses.length - 1;
    const firsts = deviceClasses.slice(0, splitIndex);
    const remaining = deviceClasses.length - splitAt;
    const last = remaining > 1 ? `${remaining} others` : deviceClasses[deviceClasses.length - 1];
    return [firsts, last];
  }

  const renderClassesText = () => {
    const [firsts, last] = chunkDeviceClasses(props.deviceClasses);

    if (!firsts.length) {
      return '';
    }

    return (
      <Text>
        {firsts
          .map<React.ReactNode>((name) => (
            <Text key={name} weight="semibold">
              {name}
            </Text>
          ))
          .reduce((prev, curr) => [prev, ', ', curr])}
        {last ? ' and ' : ''}
        <Text weight="semibold">{last}</Text>
      </Text>
    );
  };

  const renderDeviceWithClassesText = () => {
    return (
      <>
        {' '}
        with the {pluralize('class', props.deviceClasses.length)} {renderClassesText()}
      </>
    );
  };

  function renderLargeDevice() {
    return (
      <div className="ClaimDeviceClasses-deviceAddress" data-testid="large-device-display">
        <Text size="large" weight="semibold">
          {props.deviceName}
        </Text>
      </div>
    );
  }

  function renderInlineDevice() {
    return (
      <Text element="span" weight="bold">
        {props.deviceName + ' '}
      </Text>
    );
  }

  return (
    <div className="ClaimDeviceClasses">
      {props.displayLargeDevice && renderLargeDevice()}
      <Text size="small" weight="normal" data-testid={'claim-device-classes-text'}>
        {!props.displayLargeDevice && renderInlineDevice()}
        is associated with an existing Remind account
        {hasClasses() && renderDeviceWithClassesText()}
        {'. '}
        This could be a duplicate account <strong>OR</strong> belong to another Remind user.{' '}
        <Link weight={'semibold'} href={LinkConstants.CLAIM_DEVICE_HELP} target="_blank" noWrap>
          Learn More
        </Link>
      </Text>
    </div>
  );
}
